import { useState, useEffect, useRef } from "react";
import { useNavigate, Link , useOutletContext} from "react-router-dom";
import api from "../api/api";
import { getProfileCached } from "../api/profileApi";
import "../style/index.css";
import LeftSidebar from "../components/sidebar";
import Header from "../components/Header";

/* ---------- Helpers ---------- */
const parseFlightSegments = (description = '') => {
  const lines = description.split('\n').map(l => l.trim()).filter(Boolean);
  const days = [];
  let currentDay = null;

  lines.forEach((line) => {

    const rptMatch = line.match(/^Rpt-\s*(\d+)\s*(\d{2}[A-Z]{3})/);

    if (rptMatch) {
      currentDay = {
        flights: [],
        layover: null
      };
      days.push(currentDay);
      return;
    }

    const flightMatch = line.match(
      /^(DL\d+)\s+([A-Z]{3})-([A-Z]{3})/
    );

    if (flightMatch && currentDay) {
      currentDay.flights.push({
        from: flightMatch[2],
        to: flightMatch[3]
      });
      return;
    }

    const layoverMatch = line.match(/^LAYOVER\s+\d{2}:\d{2}\/([A-Z]{3})/);

    if (layoverMatch && currentDay) {
      currentDay.layover = {
        location: layoverMatch[1]
      };
    }
  });

  return days;
};

const extractCities = (eventData) => {
  if (!eventData?.description) return "--";

  const segments = parseFlightSegments(eventData.description);
  const airports = new Set();

  segments.forEach(day => {
    day.flights.forEach(f => {
      airports.add(f.from);
      airports.add(f.to);
    });

    if (day.layover?.location) {
      airports.add(day.layover.location);
    }
  });

  return airports.size ? [...airports].join(", ") : "--";
};

const calculateDutyDays = (start, end) => {
  if (!start || !end) return 0;

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0;
  }

  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((endDate - startDate) / oneDay) + 1;
};

/* ---------- Dashboard ---------- */
export default function Dashboard() {
  const navigate = useNavigate();

  const [successMessage, setSuccessMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const eventSourceRef = useRef(null);
  const currentPageRef = useRef(1);
  const outletContext = useOutletContext() || {};
  const { routeLoading = false } = outletContext;
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const [eventsData, setEventsData] = useState({
    today_events: { count: 0, events: [], page: 1, per_page: 20, total_pages: 0 },
    upcoming_events: { count: 0, events: [], page: 1, per_page: 20, total_pages: 0 },
    
  });
  const [syncBanner, setSyncBanner] = useState({
    visible: false,
    provider: "",
    processed: 0,
    status: "idle",
  });

  /* ---------- Fetch Events ---------- */
const fetchEvents = async (page = 1) => {
  setLoading(true);
  try {
    const res = await api.get("/event/dashboard/events", {
      params: {
        page: page,
        per_page: itemsPerPage
      }
    });

    const data = res.data || {};

    setCurrentPage(page);
    currentPageRef.current = page;

    setEventsData({
      today_events: data.today_events || {
        count: 0,
        events: [],
        page: 1,
        per_page: itemsPerPage,
        total_pages: 0
      },
      upcoming_events: data.upcoming_events || {
        count: 0,
        events: [],
        page: 1,
        per_page: itemsPerPage,
        total_pages: 0
      }
    });

  } catch (err) {
    console.error("Failed to fetch events", err);
  } finally {
    setLoading(false);
  }
};

const handleRemove = async (uid) => {
  if (!window.confirm("Remove this event?")) return;

  try {
    await api.delete(`/event/${uid}`);
    fetchEvents(currentPageRef.current); // refresh
  } catch (err) {
    console.error("Delete failed", err);
    alert("Failed to delete event");
  }
};

const checkSyncStatus = async () => {
  const pinnedProvider = sessionStorage.getItem("calendar_sync_provider");
  const pinnedJobId = sessionStorage.getItem("calendar_sync_job_id");
  const providers = pinnedProvider ? [pinnedProvider] : ["google", "microsoft"];

  for (const provider of providers) {
    try {
      const params = { provider };
      if (pinnedProvider === provider && pinnedJobId) {
        params.job_id = pinnedJobId;
      }
      const res = await api.get("/calendar/sync-events/status", { params });
      const data = res?.data || {};
      const status = data.status;

      if (status === "running" || status === "queued") {
        setSyncBanner({
          visible: true,
          provider,
          processed: Number(data.processed_count || 0),
          status,
        });
        return;
      }

      if (status === "success") {
        if (pinnedProvider === provider) {
          sessionStorage.removeItem("calendar_sync_provider");
          sessionStorage.removeItem("calendar_sync_job_id");
        }
        setSyncBanner({ visible: false, provider: "", processed: 0, status: "success" });
        fetchEvents(currentPageRef.current);
        continue;
      }

      if (status === "failed") {
        if (pinnedProvider === provider) {
          sessionStorage.removeItem("calendar_sync_provider");
          sessionStorage.removeItem("calendar_sync_job_id");
        }
        setSyncBanner({ visible: false, provider: "", processed: 0, status: "failed" });
        continue;
      }
    } catch {
      // Ignore transient status errors
    }
  }

  setSyncBanner((prev) => ({ ...prev, visible: false, provider: "", processed: 0 }));
};
  const startEventStream = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    const baseUrl = "/api";
    const streamUrl = `${baseUrl}/event/stream?token=${encodeURIComponent(token)}`;

    try {
      const eventSource = new EventSource(streamUrl);
      eventSourceRef.current = eventSource;

      eventSource.addEventListener("events_updated", () => {
        fetchEvents(currentPageRef.current);
      });

      eventSource.onerror = () => {
        try {
          eventSource.close();
        } catch {}
        eventSourceRef.current = null;
      };
    } catch (err) {
      console.error("Failed to start events stream", err);
    }
  };

  /* ---------- Profile ---------- */
  useEffect(() => {
    fetchEvents();
    startEventStream();
    checkSyncStatus();
    const intervalId = setInterval(checkSyncStatus, 10000);

    getProfileCached()
      .then((res) => {
        setUserName(res.data.first_name || "User");
      })
      .catch(() => setUserName("User"));

    return () => {
      clearInterval(intervalId);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

if (routeLoading) {
  return null;
}
  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-lg-3 left-sidebar-main">
           <LeftSidebar />
          </div>
          <div className="col-lg-9">
                  <Header />
            <div className="section-right">
              {syncBanner.visible && (
                <div className="alert alert-info">
                  <strong>
                    {syncBanner.provider === "google" ? "Google" : "Microsoft"} sync in progress.
                  </strong>
                  <div>Processed events: {syncBanner.processed}</div>
                  <div className="progress mt-2" role="progressbar" aria-label="Sync progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              )}
              {successMessage && (
                <div className="alert alert-success">{successMessage}</div>
              )}
              <div className="dash-section mb-4">
                  <h1>Hello, <strong>{userName}</strong></h1>
                  <p>Here is your daily activities and Events alerts</p>
              </div>
             
              <ul className="nav nav-tabs event-tab">
                <li className="nav-item">
                  <a className="nav-link active" data-bs-toggle="tab" data-bs-target="#today">
                    <img src="images/today-event.svg" className="img-fluid"/>
                    <span>Today's Event ({eventsData.today_events.count})</span>
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" data-bs-toggle="tab" data-bs-target="#upcoming">
                    <img src="images/upcoming_event.svg" className="img-fluid"/>
                    <span>Upcoming Event ({eventsData.upcoming_events.count})</span>
                  </a>
                </li>
                
              </ul>

              <div className="tab-content mt-3">
                <div className="tab-pane fade show active" id="today">
                  <h3 className="event-title mb-4">Today's Events</h3>
                  <EventCards 
                    events={eventsData.today_events.events}
                    currentPage={eventsData.today_events.page}
                    totalPages={eventsData.today_events.total_pages}
                    onPageChange={(page) => fetchEvents(page)}
					 onRemove={handleRemove}
                  />
                </div>
                <div className="tab-pane fade" id="upcoming">
                  <h3 className="event-title mb-4">Upcoming Events</h3>
                  <EventCards 
                    events={eventsData.upcoming_events.events}
                    currentPage={eventsData.upcoming_events.page}
                    totalPages={eventsData.upcoming_events.total_pages}
                    onPageChange={(page) => fetchEvents(page)}
					onRemove={handleRemove}
                  />
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      

  
  </main>
  );
}

/* ---------- Event Cards ---------- */
function EventCards({ events, currentPage = 1, totalPages = 0, onPageChange, onRemove }) {
  if (!events || events.length === 0) {
    return <div className="text-white">No events found</div>;
  }

const formatDateTime = (datetime, timezone) => {
  if (!datetime) return "N/A";

  try {
    return new Date(datetime).toLocaleString("en-US", {
      timeZone: timezone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).replace(",", "");
  } catch {
    return datetime;
  }
};

  return (
    <>
      <div className="row g-3">
        
        {events.map(ev => (
          <div key={ev.id} className="col-12">
            
            <div className="dash-section mb-4">
              <div className="row">
                <div className="col-sm-8 col-9">
                    <h5>{ev.summary || "Rotation Summary"}</h5>
                </div>
                <div className="col-sm-4 col-3">
                    <div className="dropdown three-dot-menu">
                      <button
                        className="btn btn-link p-0 border-0"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>

                      <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                          <button
							  className="dropdown-item text-danger"
							  onClick={() => onRemove(ev.uid)}
							>
                            Remove
                          </button>
                        </li>
                      </ul>
                    </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                    <div className="event-content"> Start: <span>{ev.event_type == 'rotation' ? formatDateTime(ev.report_datetime) : formatDateTime(ev.start, ev.start_timezone)} ({ev.start_timezone})</span></div>
                </div>
                <div className="col-md-6">
                    <div className="event-content">End: <span>{ev.event_type == 'rotation' ? formatDateTime(ev.release_datetime) : formatDateTime(ev.end, ev.end_timezone)} ({ev.end_timezone})</span></div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                    <div className="event-content">
                      Cities: <span>{ev.cities || extractCities(ev)}</span>
                    </div>
                </div>
                      <div className="col-md-3">
                          <div className="event-content">
                            Duty Days: <span>{ev.duty_days || calculateDutyDays(ev.start, ev.end)}</span>
                          </div>
                      </div>
                      <div className="col-md-3">
                          <div className="event-content">
                            Overnights: 
                            <span>
                              {(ev.overnights !== undefined && ev.overnights !== null)
                                ? ev.overnights
                                : parseFlightSegments(ev.description || "").filter(day => day.layover).length}
                            </span>
                          </div>
                      </div>
                  
               
                <div className="col-md-12">
                  <Link
                      to={`/rotation/${ev.uid}`}
                      className="view-btn"
                  >
                      View
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls d-flex justify-content-end align-items-center gap-3 mt-4">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          
          {/* <span className="text-white">
            Page {currentPage} of {totalPages}
          </span> */}
            {/* Page Numbers (max 2) */}
          <div className="d-flex gap-2">
            {(() => {
              let startPage = Math.max(1, currentPage - 1);
              let endPage = Math.min(totalPages, startPage + 1);

              if (endPage - startPage < 1) {
                startPage = Math.max(1, endPage - 1);
              }

              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              return pages.map((page) => (
                <button
                  key={page}
                  className={`btn btn-sm page-btn ${
                    page === currentPage ? "active" : ""
                  }`}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              ));
            })()}
          </div>
          
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
